"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, ShieldCheck, Award, House as Home, Store, Building2, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const localHighlights = [{
  icon: Home,
  title: "Residential streets",
  description: "Access scaffolding for roof replacements, chimney repairs, and extensions across Camberley neighbourhoods."
}, {
  icon: Store,
  title: "Commercial parades",
  description: "Pavement-safe scaffolding with protective fans and pedestrian tunnels for local high streets and shopfronts."
}, {
  icon: Building2,
  title: "Business parks & facilities",
  description: "Industrial access, cladding towers, and maintenance scaffolding across surrounding commercial and industrial estates."
}];
export default function LocalisedIntro() {
  const prefersReducedMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const staggerContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.12
      }
    }
  };
  return <section id="localisedintro" data-nav="light" className="relative w-full bg-background text-foreground py-16 md:py-24 overflow-hidden border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Main Copy Column */}
          <motion.div className="lg:col-span-7 flex flex-col gap-6" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeInVariants}>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 py-1 px-3 bg-muted/60">
                <MapPin className="size-3.5 text-primary" />
                <span>Camberley & Surrey</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 py-1 px-3 bg-muted/60">
                <Award className="size-3.5 text-primary" />
                <span>20+ years experience</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 py-1 px-3 bg-muted/60">
                <ShieldCheck className="size-3.5 text-primary" />
                <span>NASC & CHAS accredited</span>
              </Badge>
            </div>

            <div className="space-y-4">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
                Scaffolding in Camberley
              </h2>
              <p className="text-base sm:text-lg font-medium text-foreground leading-relaxed">
                BMF Scaffolding provides commercial, residential, roofing, solar and specialist scaffolding in Camberley and the surrounding Surrey area, including GU15.
              </p>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                BMF Scaffolding Ltd delivers safe, fully compliant access solutions
                across Camberley and the surrounding Surrey area. Whether you manage
                works on suburban residential avenues, busy town commercial parades,
                or facilities throughout local business parks, our experienced team
                handles every stage from structural planning to secure erection and
                prompt dismantling.
              </p>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Led by Pete Wood with more than 20 years in the scaffolding industry,
                we provide dependable scaffolding support for homeowners, roofing
                contractors, solar installers, and commercial property managers. Every
                structure is erected to strict NASC guidelines and audited under CHAS
                standards for complete site safety.
              </p>
            </div>

            {/* Accreditation & Quality bullets */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <div className="flex items-center gap-2.5 text-sm text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>NASC full safety compliance</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>CHAS advanced accredited</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Direct oversight by Pete Wood</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Fast local response & site surveys</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-4">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/ContactRequestQuotePage">
                  Request a Camberley quote
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/AreasWeCoverPage">View all areas covered</Link>
              </Button>
            </div>
          </motion.div>

          {/* Local Sector Context Cards Column */}
          <motion.div className="lg:col-span-5 flex flex-col gap-4" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={staggerContainer}>
            <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
              <h2 className="text-base font-semibold text-foreground mb-1">
                Local project environments
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground mb-4">
                Tailored scaffolding built to respect local Surrey planning and access conditions.
              </p>

              <div className="flex flex-col gap-3.5">
                {localHighlights.map((item, index) => {
                const IconComponent = item.icon;
                return <motion.div key={item.title} data-index={index} variants={fadeInVariants}>
                      <Card className="border border-border/80 bg-background/50 hover:bg-background transition-[color,background-color,border-color,box-shadow] duration-200">
                        <CardContent className="p-4 flex items-start gap-3.5">
                          <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0">
                            <IconComponent className="size-5" />
                          </div>
                          <div className="space-y-1">
                            <h3 className="text-sm font-semibold text-foreground leading-tight">
                              {item.title}
                            </h3>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>;
              })}
              </div>

              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span>Surrey & Home Counties coverage</span>
                <Link to="/HealthSafetyAccreditationsPage" className="text-primary font-medium hover:underline inline-flex items-center gap-1">
                  Our standards
                  <ArrowRight className="size-3" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}